import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { isRecord, RegistrationKind, RegistrationQuote, RegistrationResult } from '../domain/registration-command';

export class RegistrationRequestError extends Error {
  constructor(readonly reason: 'unavailable' | 'invalid-response' | 'expired-reservation') { super(reason); }
}

@Injectable({ providedIn: 'root' })
export class RegistrationApi {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl;
  private async read(url: string): Promise<unknown> {
    const response = await firstValueFrom(this.http.get<unknown>(this.base + url).pipe(timeout(15000)));
    if (!isRecord(response) || response['success'] !== true) throw new RegistrationRequestError('invalid-response');
    return response['result'];
  }
  async quote(planId: number, startDate: string, paymentMethod: string): Promise<RegistrationQuote> {
    const result = await this.read(`/studio/plan/${planId}/start_date/${startDate}/${paymentMethod}`);
    if (!isRecord(result) || !Array.isArray(result['firstInvoice']) || typeof result['firstInvoiceDate'] !== 'string') throw new RegistrationRequestError('invalid-response');
    const lines = result['firstInvoice'].map(value => {
      if (!isRecord(value) || typeof (value['title'] ?? value['name']) !== 'string' || typeof value['amount'] !== 'number' || !Number.isFinite(value['amount'])) throw new RegistrationRequestError('invalid-response');
      return { title: String(value['title'] ?? value['name']), amount: value['amount'] };
    });
    return { firstInvoice: lines, firstInvoiceDate: result['firstInvoiceDate'], total: lines.reduce((sum, line) => sum + line.amount, 0) };
  }
  async emailExists(email: string | undefined): Promise<boolean> {
    const r = await firstValueFrom(this.http.post<unknown>(this.base + '/member/address', { checkString: email }).pipe(timeout(15000)));
    if (!isRecord(r) || r['success'] !== true || typeof r['result'] !== 'boolean') throw new RegistrationRequestError('invalid-response');
    return r['result'];
  }
  async register(kind: RegistrationKind, payload: Record<string, unknown>, password?: string): Promise<RegistrationResult> {
    const path = kind === 'new_membership' ? '/member/new' : kind === 'temporary_registration' ? '/member/temp/new' : '/member/temp/register/' + encodeURIComponent(password ?? '');
    const r = await firstValueFrom(this.http.post<unknown>(this.base + path, payload).pipe(timeout(60000)));
    if (kind === 'new_membership' && isRecord(r) && r['success'] === false && r['error'] === 'expiredTempReserve') throw new RegistrationRequestError('expired-reservation');
    if (!isRecord(r) || r['success'] !== true || !isRecord(r['result'])) throw new RegistrationRequestError('invalid-response');
    const result = r['result'];
    const id = result['id'] ?? result['memberId'];
    if ((typeof id !== 'number' && typeof id !== 'string') || !Number.isSafeInteger(Number(id)) || Number(id) <= 0) throw new RegistrationRequestError('invalid-response');
    return {
      memberId: Number(id), emailSent: !!result['messageId'],
      gmoCreateError: !!(result['createGmoMember'] && (result['createGmoMember'] !== 'success' || !result['saveCreditCard'])) || !!(result['saveCreditCard'] && result['saveCreditCard'] !== 'success'),
      imageKey: typeof result['tempPassword'] === 'string' ? result['tempPassword'] : undefined,
    };
  }
  async upload(memberId: number, index: number, key: string, file: File): Promise<void> {
    const form = new FormData(); form.append('file', file);
    const r = await firstValueFrom(this.http.post<unknown>(`${this.base}/member/${memberId}/picture/${index}/key/${encodeURIComponent(key)}`, form).pipe(timeout(30000)));
    if (!isRecord(r) || r['success'] !== true) throw new RegistrationRequestError('invalid-response');
  }
  async clearExpiredReservations(ids: number[]): Promise<void> {
    if (ids.length) await this.read('/reservation/record/visit/delete/' + ids.join(','));
  }
  async deleteExpiredPassword(): Promise<void> { await this.read('/member/temp/pass/delete'); }
}
